import { FC } from 'react';

const CoreMemberPage: FC = () => {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>

      {/* Hero Section - White bg with right accent image */}
      <section className="relative py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-6 text-center lg:text-left">
            <h1 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight">
              実務家・即戦力限定
            </h1>
            <p className="text-xl md:text-2xl font-bold text-gray-700">
              ダイナビジョン・バックオフィス<br />
              選抜メンバー募集
            </p>
          </div>
          <div className="w-full h-72 lg:h-96 rounded-lg overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/78de93bb0604298afcd7b23e7b9e6a3b.jpg"
              alt="バックオフィス"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Important Notice - Callout box */}
      <section className="py-8 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#e8f0f6] p-6 rounded-lg border-l-4 border-[#5b8faa]">
            <h4 className="text-lg font-bold text-gray-900 mb-3">
              【重要】今回は即戦力候補の募集のため未経験から学びたい方のエントリーはご遠慮下さい。
            </h4>
            <p className="text-gray-700 leading-relaxed text-sm">
              本募集は実務経験を持つプロフェッショナルのみを対象としている。成長機会を求める方ではなく、即座に成果を出せる人材を求めている。
            </p>
          </div>
        </div>
      </section>

      {/* Guardian Section - Blue gradient background */}
      <section className="relative py-32 px-6">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20navy%20blue%20background%20with%20subtle%20golden%20light%20rays%20streaming%20diagonally%2C%20creating%20a%20sense%20of%20authority%20and%20gravitas%2C%20minimalist%20corporate%20aesthetic%20with%20deep%20shadows%20and%20warm%20highlights%2C%20conveying%20strength%20and%20protection%2C%20elegant%20and%20powerful%20atmosphere&width=1400&height=600&seq=coremember002&orientation=landscape"
            alt="守護者"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#3d6b8a]/85 via-[#4a7c9b]/80 to-[#3d6b8a]/85"></div>
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-wide">
            求む、ダイナビジョンの守護者
          </h2>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto space-y-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 leading-relaxed">
            あなたの実務スキルで、スピリチュアルを社会に<br />届ける「最強の裏方」を募集します。
          </h2>
          <p className="text-base text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            世界最高峰のスピリチュアル・コンテンツを、確実に届けるための「道」を構築する。それは祈りではなく、論理と実務によって実現される。あなたの専門性が、人生を変えるメッセージの架け橋となる。
          </p>
        </div>
      </section>

      {/* Entry CTA - First (2 column with image) */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-5">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                実務選抜・説明会にエントリーする
              </h2>
              <p className="text-sm text-gray-600">※選考には「思考課題」の提出が含まれる</p>
              <p className="text-base text-gray-700 leading-relaxed">
                プロフェッショナルとしての判断力と論理的思考を評価する、厳格な審査プロセスを経て、真の実務家のみを選抜する。
              </p>
              <a
                href="https://forms.gle/zPabNkE36yPhwBvRA"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-block bg-[#4a7c9b] hover:bg-[#3d6b8a] text-white font-bold text-base px-8 py-3 rounded-md shadow-md transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                エントリーフォームはこちら
              </a>
            </div>
            <div className="w-full h-64 rounded-lg overflow-hidden shadow-md">
              <img
                src="https://readdy.ai/api/search-image?query=A%20clean%20modern%20workspace%20with%20a%20laptop%20showing%20data%20analytics%20dashboard%2C%20professional%20desk%20setup%20with%20organized%20documents%20and%20a%20cup%20of%20coffee%2C%20soft%20natural%20light%20from%20a%20window%2C%20cool%20toned%20minimalist%20office%20environment%20conveying%20productivity%20and%20precision%2C%20steel%20blue%20and%20white%20color%20palette&width=600&height=400&seq=coremember003&orientation=landscape"
                alt="エントリー"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Logic & Practice */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto space-y-10">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900">
            魔法を届けるのは、論理と実務である。
          </h2>

          <div className="space-y-4 text-base text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            <p>ダイナビジョンには、人生を変えるメッセージと、世界最高峰のスピリチュアル・コンテンツがある。</p>
            <p>しかし、どんなに素晴らしい光も、届ける「道」がなければ誰にも届かない。</p>
            <p>その「道」を作るのは、祈りではなく<strong>「実務」</strong>である。</p>
          </div>

          {/* 4 Skill Cards - matching original style with light blue-gray bg */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="bg-[#e8f0f6] p-6 rounded-lg text-center">
              <h4 className="text-base font-bold text-gray-900 mb-2">寸分の狂いもない配信設定</h4>
              <p className="text-sm text-gray-600">正確性と信頼性を担保する技術力</p>
            </div>
            <div className="bg-[#e8f0f6] p-6 rounded-lg text-center">
              <h4 className="text-base font-bold text-gray-900 mb-2">先回りした顧客対応</h4>
              <p className="text-sm text-gray-600">不安を解消する高度なアナウンス能力</p>
            </div>
            <div className="bg-[#e8f0f6] p-6 rounded-lg text-center">
              <h4 className="text-base font-bold text-gray-900 mb-2">データ最適化の管理能力</h4>
              <p className="text-sm text-gray-600">膨大な情報を整理し、価値を生み出す</p>
            </div>
            <div className="bg-[#e8f0f6] p-6 rounded-lg text-center">
              <h4 className="text-base font-bold text-gray-900 mb-2">論理的な危機管理</h4>
              <p className="text-sm text-gray-600">感情ではなく、冷静な判断力で動く</p>
            </div>
          </div>

          <p className="text-base text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            私たちが今探しているのは、ヒーラーではない。PCという魔法の杖を使いこなし、ダイナビジョンの世界観を「システムとオペレーション」で支える、プロフェッショナルな実務家である。
          </p>
        </div>
      </section>

      {/* Not a Learning Place - Blue-gray bg section */}
      <section className="relative py-28 px-6 bg-[#dce8f0]">
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
            これは
          </h2>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
            「学びの場」ではない。
          </h2>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
            「活躍の場」である。
          </h2>
        </div>
      </section>

      {/* Frustration Cards */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto space-y-10">
          <p className="text-base text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            正直にお伝えする。今回の募集は「ダイナビジョンで働けば癒やされる」「事務を教えてもらえる」という方には全く向かない。逆に、以下のようなフラストレーションを抱えている方には、最高の舞台となる。
          </p>

          {/* 3 cards with outline style matching original */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white p-6 rounded-lg border-2 border-[#b0c8d8] relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#5b8faa]"></div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">違和感を抱いてきた</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                「スピリチュアルは好きだが、ふわふわした運営には違和感がある」
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border-2 border-[#b0c8d8] relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#5b8faa]"></div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">意味を求めている</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                「一般企業で培ったPCスキルや管理能力を、もっと意味のあることに使いたい」
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border-2 border-[#b0c8d8] relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#5b8faa]"></div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">自律性に喜びを感じる</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                「指示を待つのではなく、自分で仕組みを整えることに喜びを感じる」
              </p>
            </div>
          </div>

          {/* Quote block */}
          <div className="border-l-4 border-[#5b8faa] pl-6 py-4 bg-[#f4f8fb] rounded-r-lg">
            <p className="text-base text-gray-800 leading-relaxed italic">
              あなたのそのExcelスキルは、あなたのその文章作成能力は、誰かの人生を覚醒させるためにこそ、あるべきである。
            </p>
          </div>
        </div>
      </section>

      {/* Entry Requirements - Left content + Right images */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-3 space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">エントリー要件</h2>
                <p className="text-base text-gray-600 font-bold">(必ずご確認ください)</p>
              </div>

              <p className="text-base text-gray-700">
                今回は<strong>「即戦力候補」</strong>の募集である。<br />
                以下の基準を満たす方のみ、エントリーいただける。
              </p>

              {/* Requirement 01 */}
              <div className="bg-[#f4f8fb] p-6 rounded-lg">
                <div className="flex items-start mb-3">
                  <span className="bg-[#4a7c9b] text-white text-xs font-bold w-7 h-7 flex items-center justify-center rounded-full mr-3 mt-0.5 shrink-0">01</span>
                  <h4 className="text-lg font-bold text-gray-900">PC環境・ITリテラシー(必須)</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-700 ml-10">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>ご自身専用のPCを所有していること(スマホのみ不可)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>安定したネット回線があり、Zoomでのビデオ通話がスムーズに行えること</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>新しいツールを導入する際、自ら検索して使い方を習得できる「検索力」があること</span>
                  </li>
                </ul>
              </div>

              {/* Requirement 02 */}
              <div className="bg-[#f4f8fb] p-6 rounded-lg">
                <div className="flex items-start mb-3">
                  <span className="bg-[#4a7c9b] text-white text-xs font-bold w-7 h-7 flex items-center justify-center rounded-full mr-3 mt-0.5 shrink-0">02</span>
                  <h4 className="text-lg font-bold text-gray-900">実務経験(以下のいずれかの経験者を優遇)</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-700 ml-10">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>Googleスプレッドシート(関数・ピボットテーブル等)の実務利用</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>Zoomホストとしての運営経験</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>CRM(顧客管理システム)やメルマガ配信スタンドの運用経験</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>WordPressの入稿・管理</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 shrink-0">•</span>
                    <span>カスタマーサポート(メール・チャット対応)の実務経験</span>
                  </li>
                </ul>
              </div>

              {/* Requirement 03 */}
              <div className="bg-[#f4f8fb] p-6 rounded-lg">
                <div className="flex items-start mb-3">
                  <span className="bg-[#4a7c9b] text-white text-xs font-bold w-7 h-7 flex items-center justify-center rounded-full mr-3 mt-0.5 shrink-0">03</span>
                  <h4 className="text-lg font-bold text-gray-900">マインドセット</h4>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed ml-10">
                  スピリチュアルへの理解と、ビジネスとしての実務を分け隔てなく扱えること。知性と実務能力の両立が求められる。
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-5">
              <div className="w-full h-72 rounded-lg overflow-hidden shadow-md">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20focused%20professional%20woman%20working%20at%20a%20modern%20desk%20with%20multiple%20monitors%20showing%20spreadsheets%20and%20data%2C%20organized%20workspace%20with%20notebooks%20and%20digital%20tools%2C%20natural%20light%20from%20a%20window%2C%20conveying%20competence%20and%20dedication%2C%20cool%20neutral%20tones%20with%20subtle%20warm%20accents%2C%20corporate%20professional%20atmosphere&width=500&height=600&seq=coremember005&orientation=portrait"
                  alt="プロフェッショナル"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="w-full h-72 rounded-lg overflow-hidden shadow-md">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20office%20meeting%20room%20with%20a%20large%20screen%20displaying%20project%20management%20tools%20and%20timelines%2C%20clean%20minimalist%20interior%20with%20plants%20and%20natural%20light%2C%20professional%20team%20collaboration%20space%2C%20cool%20blue%20and%20white%20tones%20conveying%20organization%20and%20efficiency&width=500&height=600&seq=coremember006&orientation=portrait"
                  alt="チーム環境"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Selection - Serious Only (with right image) */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                本気の方だけを選抜する。
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                私たちは、あなたの時間を無駄にしない。そのため、最初の段階から<strong>「実務視点」</strong>での選考を行う。真のプロフェッショナルを見極めるための、厳格かつ公正なプロセスを設計している。
              </p>
            </div>
            <div className="w-full h-64 rounded-lg overflow-hidden shadow-md">
              <img
                src="https://readdy.ai/api/search-image?query=A%20dramatic%20close%20up%20of%20hands%20typing%20on%20a%20premium%20laptop%20keyboard%20with%20soft%20bokeh%20lights%20in%20the%20background%2C%20dark%20moody%20atmosphere%20with%20warm%20amber%20accent%20lighting%2C%20conveying%20intensity%20and%20focus%2C%20professional%20determination%2C%20cinematic%20quality%20with%20shallow%20depth%20of%20field&width=1400&height=600&seq=coremember007&orientation=landscape"
                alt="本気の選抜"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Selection Flow */}
      <section className="py-20 px-6 bg-[#dce8f0]">
        <div className="max-w-5xl mx-auto space-y-10">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900">選考フロー</h2>

          {/* Flow Icons Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-6">
          </div>

          <p className="text-base text-gray-700 text-center">
            各段階で実務能力と論理的思考力を評価し、真の実務家のみを選抜する。
          </p>

          {/* Timeline Steps */}
          <div className="space-y-0 max-w-3xl mx-auto">
            <div className="relative pl-10 pb-10 border-l-2 border-[#5b8faa] ml-4">
              <div className="absolute -left-2.5 top-0 w-5 h-5 bg-[#4a7c9b] rounded-full"></div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="text-base font-bold text-gray-900 mb-2">STEP 1: エントリー審査(書類・課題)</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  フォームにてスキルチェックと、<strong>記述式の「思考課題」</strong>に回答いただく。単なる意欲ではなく「トラブル時の対応力」や「論理的思考」を拝見する。
                </p>
              </div>
            </div>

            <div className="relative pl-10 pb-10 border-l-2 border-[#5b8faa] ml-4">
              <div className="absolute -left-2.5 top-0 w-5 h-5 bg-[#4a7c9b] rounded-full"></div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="text-base font-bold text-gray-900 mb-2">STEP 2: 実務選抜ブリーフィング(Zoom)</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  書類審査を通過された方限定で、オンライン選考会へご招待する。業務のリアルな裏側、泥臭い部分も全て公開する。また、その場で<strong>「逆質問」</strong>の時間も設ける。あなたのプロとしての視点で、私たちに質問を投げかけてください。
                </p>
              </div>
            </div>

            <div className="relative pl-10 pb-10 border-l-2 border-[#5b8faa] ml-4">
              <div className="absolute -left-2.5 top-0 w-5 h-5 bg-[#4a7c9b] rounded-full"></div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="text-base font-bold text-gray-900 mb-2">STEP 3: 実務トライアル(1〜2週間)</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  実際に報酬をお支払いし、実務タスク(課題)に取り組んでいただく。期間中、一定の<strong>「プレッシャー(負荷)」</strong>がかかる場面もある。そこでの対応力を見て、最終的なパートナーシップを決定する。
                </p>
              </div>
            </div>

            <div className="relative pl-10 ml-4">
              <div className="absolute -left-2.5 top-0 w-5 h-5 bg-[#4a7c9b] rounded-full"></div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="text-base font-bold text-gray-900 mb-2">STEP 4: パートナー契約締結</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  ダイナビジョン・バックオフィスチームの一員として正式にお迎えする。プロフェッショナルとしての誇りを持ち、共に最強のチームを作る。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Representative Message */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
            表舞台の光を、裏側から支える誇りを。
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1">
              <div className="text-center lg:text-left">
                <p className="text-base font-bold text-gray-900 mb-1">株式会社ダイナビジョン</p>
                <p className="text-sm text-gray-600 font-bold">代表メッセージ</p>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-5 text-base text-gray-700 leading-relaxed">
              <p>
                スピリチュアルを「怪しいもの」で終わらせず、「現実を変える力」として社会に定着させる。それがダイナビジョンの使命である。
              </p>
              <p>
                そのためには、強固な土台が必要である。あなたの事務処理能力、ITスキル、そして冷静な判断力。それこそが、私たちが今、最も必要としている「光」である。
              </p>
              <div className="border-l-4 border-[#5b8faa] pl-6 py-4 bg-[#f4f8fb] rounded-r-lg">
                <p className="text-base text-gray-800 leading-relaxed italic">
                  「我こそは」と思う方。その力を、貸してください。最強のチームを作りましょう。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Entry Section - with background image */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20wide%20panoramic%20view%20of%20a%20modern%20glass%20building%20exterior%20at%20golden%20hour%20with%20warm%20sunlight%20reflecting%20off%20the%20windows%2C%20creating%20a%20sense%20of%20new%20beginnings%20and%20professional%20opportunity%2C%20clean%20architectural%20lines%20against%20a%20gradient%20sky%20transitioning%20from%20warm%20amber%20to%20cool%20slate%2C%20inspiring%20and%20forward-looking%20atmosphere&width=1400&height=700&seq=coremember008&orientation=landscape"
            alt="エントリー"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2c4f6b]/85 via-[#3d6b8a]/80 to-[#2c4f6b]/85"></div>
        </div>
        <div className="relative max-w-4xl mx-auto">
          <div className="text-center space-y-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white leading-relaxed">
              プロフェッショナル選抜<br />ダイナビジョン・バックオフィス エントリー
            </h2>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 max-w-2xl mx-auto text-left">
              <h4 className="text-base font-bold text-white mb-3">【ご注意】</h4>
              <ul className="space-y-2 text-sm text-white/90">
                <li className="flex items-start">
                  <span className="mr-2 mt-0.5 shrink-0">•</span>
                  <span>本フォームは「思考課題」を含むため、回答に10〜15分程度かかる</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-0.5 shrink-0">•</span>
                  <span>PCからの入力を強く推奨する</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-0.5 shrink-0">•</span>
                  <span>提出された課題は、論理的思考力と実務対応力の評価に使用される</span>
                </li>
              </ul>
            </div>

            <p className="text-sm text-white/90 leading-relaxed max-w-2xl mx-auto">
              真のプロフェッショナルのみが、この門をくぐることができる。あなたの実務能力と知性を、厳格な審査プロセスで証明していただく。
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA - Dark blue section */}
      <section className="py-16 px-6 bg-[#2c4f6b]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-5">
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                エントリー審査に進む
              </h2>
              <p className="text-sm text-white/80 leading-relaxed">
                実務家としての誇りを持ち、ダイナビジョンの守護者となる決意がある方のみ、次のステップへ。
              </p>
              <a
                href="https://forms.gle/zPabNkE36yPhwBvRA"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-block bg-[#4a7c9b] hover:bg-[#5b8faa] text-white font-bold text-base px-8 py-3 rounded-md shadow-md transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                エントリーフォームへ進む
              </a>
            </div>
            <div className="space-y-3">
              <div className="w-full h-56 rounded-lg overflow-hidden shadow-md">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20team%20of%20professionals%20gathered%20around%20a%20modern%20conference%20table%20with%20laptops%20and%20documents%2C%20collaborative%20atmosphere%20with%20warm%20ambient%20lighting%2C%20diverse%20group%20working%20together%20with%20focus%20and%20determination%2C%20cool%20neutral%20office%20tones%20with%20subtle%20warm%20accents%2C%20conveying%20teamwork%20and%20shared%20mission&width=600&height=400&seq=coremember009&orientation=landscape"
                  alt="チーム"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <p className="text-white/60 text-xs leading-relaxed">
                選ばれし実務家として、スピリチュアルを社会に届ける「最強の裏方」の一員となる。論理と実務で、光を届ける道を共に作る。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 bg-[#243f54]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/40 text-xs">
            &copy; {new Date().getFullYear()} 株式会社ダイナビジョン All Rights Reserved. ・ <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="hover:text-white/60 cursor-pointer transition-colors">Powered by Readdy</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CoreMemberPage;
